/**
 * @file WriteoffDraftForm.tsx
 * @description Создание черновика списания: склад + причина + комментарий
 * (вынесено из страницы списания, phase19.13 этап 0; общий стиль и
 * `SelectField` вместо нативных select — phase19.23, A2/A3).
 *
 * phase19.31 (зона «Склад», минимум): заголовок «Новое списание» с «назад» в
 * верхней панели, «Создать черновик» — главное действие там же обычной
 * градиентной кнопкой (черновик — не опасное действие); поля до 720px.
 */

import { useEffect, useState } from "react";

import { Button } from "../ui/button";
import { CabinetPageHeader } from "../ui/CabinetPageHeader";
import { Textarea } from "../ui/textarea";
import { SelectField } from "../ui/SelectField";
import { useNotify } from "../ui/NotifyProvider";
import { WmsFieldLabel } from "./WmsFieldLabel";
import { ApiError } from "../../lib/api-client";
import {
  WRITEOFF_REASON_LABEL,
  warehousesApi,
  writeoffsApi,
  type WarehouseDto,
  type WriteoffDto,
  type WriteoffReason,
} from "../../lib/api-wms";

/** Реестр причин: новая причина = запись в WRITEOFF_REASON_LABEL — и всё. */
const REASON_OPTIONS = Object.entries(WRITEOFF_REASON_LABEL).map(
  ([value, label]) => ({ value, label }),
);

interface Props {
  onCreated: (writeoff: WriteoffDto) => void;
  /** Реестр списаний — «назад». */
  backTo: string;
}

export function WriteoffDraftForm({ onCreated, backTo }: Props) {
  const notify = useNotify();
  const [warehouses, setWarehouses] = useState<WarehouseDto[]>([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [reason, setReason] = useState<WriteoffReason>("EXPIRED");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    void warehousesApi.list().then((data) => {
      setWarehouses(data);
      const def = data.find((w) => w.isDefault) ?? data[0];
      if (def) setWarehouseId(def.id);
    });
  }, []);

  async function handleCreate() {
    if (!warehouseId) return;
    if (reason === "OTHER" && !notes.trim()) {
      notify.error("Для причины «Другое» комментарий обязателен");
      return;
    }
    setCreating(true);
    try {
      const created = await writeoffsApi.create({
        warehouseId,
        reason,
        notes: notes.trim() || undefined,
      });
      notify.success("Черновик создан");
      onCreated(created);
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="flex max-w-[45rem] flex-col gap-4">
      <CabinetPageHeader
        title="Новое списание"
        subtitle="Черновик остаток не двигает: товар спишется после проведения"
        back={{ to: backTo, label: "К списаниям" }}
        action={
          <Button variant="brand" onClick={() => void handleCreate()} disabled={!warehouseId || creating} loading={creating}>
            Создать черновик
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <WmsFieldLabel label="Склад">
          <SelectField
            value={warehouseId}
            onChange={setWarehouseId}
            options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            placeholder="— склад —"
            ariaLabel="Склад списания"
          />
        </WmsFieldLabel>

        <WmsFieldLabel label="Причина">
          <SelectField
            value={reason}
            onChange={(next) => setReason(next as WriteoffReason)}
            options={REASON_OPTIONS}
            ariaLabel="Причина списания"
          />
        </WmsFieldLabel>
      </div>

      <WmsFieldLabel
        label={reason === "OTHER" ? "Комментарий (обязательно)" : "Комментарий"}
      >
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Что случилось с товаром"
        />
      </WmsFieldLabel>
    </div>
  );
}
